import type { Game } from '../../shared/Game.js';
import { draw } from './BalloonRenderer.js';
import { createState, isComplete, popAny, popAt, resize, step } from './BalloonSimulation.js';
import type { BalloonSkin } from './BalloonSkin.js';

const MAX_DPR = 2;
const MAX_DT = 0.05;
const COMPLETE_DELAY_MS = 700;

export const BalloonGame: Game<BalloonSkin> = {
  mount(canvas, { skin, target, reducedMotion, onProgress, onComplete }) {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      onComplete();
      return () => {};
    }

    const size = () => canvas.getBoundingClientRect();
    const initial = size();
    const state = createState({
      width: initial.width,
      height: initial.height,
      target,
      colors: skin.colors,
      particleColors: skin.particleColors,
      reducedMotion,
    });

    const applySize = () => {
      const rect = size();
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      resize(state, rect.width, rect.height);
    };
    applySize();

    const observer = new ResizeObserver(applySize);
    observer.observe(canvas);

    let completeTimer: number | undefined;
    const afterPop = () => {
      onProgress(state.popped, state.target);
      if (isComplete(state) && completeTimer === undefined) {
        completeTimer = window.setTimeout(onComplete, COMPLETE_DELAY_MS);
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (popAt(state, event.clientX - rect.left, event.clientY - rect.top)) {
        afterPop();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (popAny(state)) {
          afterPop();
        }
      }
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('keydown', onKeyDown);

    let frame = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min((now - last) / 1000, MAX_DT);
      last = now;
      step(state, dt);
      draw(ctx, state, skin);
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);

    onProgress(0, state.target);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(completeTimer);
      observer.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('keydown', onKeyDown);
    };
  },
};
